import React, { useState } from 'react';
import styles from './LeagueOverallRanking.module.css';

const LeagueOverallRanking = ({ rankings, config, groups }) => {
    const [selectedPlayer, setSelectedPlayer] = useState(null);

    if (!rankings || rankings.length === 0) return null;

    const openBreakdownModal = (player) => setSelectedPlayer(player);
    const closeBreakdownModal = () => setSelectedPlayer(null);

    const uniqueGroups = new Set(rankings.map(r => r.currentGroup).filter(Boolean));
    const showGroupColumn = uniqueGroups.size > 1;

    return (
        <div className={styles.container}>
            <div className={styles.headerRow}>
                <h3 className={styles.title}>Ranking Ogólny</h3>
            </div>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Zawodnik</th>
                        {showGroupColumn && <th>Grupa</th>}
                        <th>M</th>
                        <th>W</th>
                        <th>P</th>
                        <th>Pkt</th>
                    </tr>
                </thead>
                <tbody>
                    {rankings.map((r) => (
                        <tr key={r.participantId} className={r.frozen ? styles.frozen : ''}>
                            <td>{r.rank}</td>
                            <td>
                                {r.frozen && <span title="Zamrożony">&#10052;&#65039; </span>}
                                <div>{r.playerName}</div>
                                {r.partnerName && <div className={styles.partnerLine}>{r.partnerName}</div>}
                            </td>
                            {showGroupColumn && <td>{r.currentGroup || '-'}</td>}
                            <td>{r.matchesPlayed}</td>
                            <td>{r.wins}</td>
                            <td>{r.losses}</td>
                            <td
                                className={styles.points}
                                onClick={() => openBreakdownModal(r)}
                                title="Kliknij, aby zobaczyć rozkład per runda"
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openBreakdownModal(r); }}
                            >
                                {r.totalWeightedPoints.toFixed(2)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {selectedPlayer && (
                <div className={styles.modalOverlay} onClick={closeBreakdownModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h4 className={styles.modalTitle}>
                                Szczegóły punktacji — {selectedPlayer.playerName}
                                {selectedPlayer.partnerName && ` / ${selectedPlayer.partnerName}`}
                            </h4>
                            <button
                                className={styles.modalClose}
                                onClick={closeBreakdownModal}
                                aria-label="Zamknij"
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            {(groups && groups.length > 0) || config ? (
                                <div className={styles.paramsPanel}>
                                    {groups && groups.length > 0 && (
                                        <div className={styles.paramRow}>
                                            <span className={styles.paramLabel}>Premia grupowa:</span>
                                            <span>
                                                {groups.map(g => `${g.groupLabel}: ${Math.round(g.groupPremium ?? 0)}`).join(' · ')}
                                            </span>
                                        </div>
                                    )}
                                    {config && (
                                        <>
                                            <div className={styles.paramRow}>
                                                <span className={styles.paramLabel}>Bonus top-3 (krok):</span>
                                                <span>{Math.round((config.placeBonusTop3Step ?? 0) * 100)}%</span>
                                            </div>
                                            <div className={styles.paramRow}>
                                                <span className={styles.paramLabel}>Kompensacja zamrożeń:</span>
                                                <span>{Math.round((config.frozenCompensationPct ?? 0) * 100)}%</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : null}
                            {(!selectedPlayer.roundBreakdown || selectedPlayer.roundBreakdown.length === 0) ? (
                                <p className={styles.emptyBreakdown}>Brak danych per rundę.</p>
                            ) : (() => {
                                const sums = selectedPlayer.roundBreakdown.reduce((acc, b) => {
                                    if (!b.frozen) {
                                        acc.matchPoints += b.matchPoints || 0;
                                        acc.groupPremium += b.groupPremium || 0;
                                        acc.placeBonus += b.placeBonus || 0;
                                    } else {
                                        acc.frozenCompensation += b.frozenCompensation || 0;
                                    }
                                    return acc;
                                }, { matchPoints: 0, groupPremium: 0, placeBonus: 0, frozenCompensation: 0 });
                                return (
                                    <table className={styles.breakdownTable}>
                                        <thead>
                                            <tr>
                                                <th>Runda</th>
                                                {showGroupColumn && <th>Grupa</th>}
                                                <th title="Rozegrane / zaplanowane">M</th>
                                                <th title="Punkty meczowe = suma / zaplanowane">Mecze</th>
                                                <th title="Premia grupowa po pro-racie">Premia</th>
                                                <th title="Premia za miejsce">Miejsce</th>
                                                <th title="Kompensacja za zamrożenie">Kompensacja</th>
                                                <th>Suma</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedPlayer.roundBreakdown.map((b) => (
                                                <tr key={b.roundNumber} className={b.frozen ? styles.breakdownFrozen : ''}>
                                                    <td>{b.roundNumber}</td>
                                                    {showGroupColumn && <td>{b.groupLabel || '-'}</td>}
                                                    <td>{b.notParticipating || b.frozen ? '—' : `${b.matchesPlayed}/${b.matchesPlanned}`}</td>
                                                    <td>{b.notParticipating || b.frozen ? '—' : b.matchPoints.toFixed(2)}</td>
                                                    <td>{b.notParticipating || b.frozen ? '—' : b.groupPremium.toFixed(2)}</td>
                                                    <td>{b.notParticipating || b.frozen ? '—' : b.placeBonus.toFixed(2)}</td>
                                                    <td>{b.notParticipating ? '—' : (b.frozen ? b.frozenCompensation.toFixed(2) : '—')}</td>
                                                    <td className={styles.breakdownRowTotal}>
                                                        {b.notParticipating ? '—' : b.roundTotal.toFixed(2)}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr className={styles.breakdownTotalRow}>
                                                <td colSpan={showGroupColumn ? 3 : 2}>Razem</td>
                                                <td>{sums.matchPoints.toFixed(2)}</td>
                                                <td>{sums.groupPremium.toFixed(2)}</td>
                                                <td>{sums.placeBonus.toFixed(2)}</td>
                                                <td>{sums.frozenCompensation.toFixed(2)}</td>
                                                <td className={styles.breakdownRowTotal}>
                                                    {selectedPlayer.totalWeightedPoints.toFixed(2)}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeagueOverallRanking;
